import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area, BarChart, Bar, Legend } from "recharts";
import { TrendingUp, TrendingDown, Minus, FileDown, Calendar, Target, Activity } from "lucide-react";
import { format, subMonths, parseISO } from "date-fns";
import { useToast } from "@/hooks/use-toast";

interface VisualProgressReportProps {
  clientId: string;
  clinicianId: string;
}

interface AssessmentData {
  assessment_type: string;
  total_score: number;
  administered_at: string;
  severity_level: string;
}

interface SessionRating {
  created_at: string;
  mood_before: number | null;
  mood_after: number | null;
  session_helpfulness: number | null;
  therapeutic_alliance: number | null;
}

export function VisualProgressReport({ clientId, clinicianId }: VisualProgressReportProps) {
  const { toast } = useToast();
  const [assessments, setAssessments] = useState<AssessmentData[]>([]);
  const [sessionRatings, setSessionRatings] = useState<SessionRating[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedAssessment, setSelectedAssessment] = useState<string>("all");
  const [timeRange, setTimeRange] = useState<string>("6m");

  useEffect(() => {
    if (clientId) {
      fetchProgressData();
    }
  }, [clientId, timeRange]);

  const fetchProgressData = async () => {
    setLoading(true);
    const monthsBack = timeRange === "3m" ? 3 : timeRange === "6m" ? 6 : 12;
    const startDate = subMonths(new Date(), monthsBack).toISOString();

    const [assessmentsRes, ratingsRes] = await Promise.all([
      supabase
        .from("clinical_assessments")
        .select("assessment_type, total_score, administered_at, severity_level")
        .eq("client_id", clientId)
        .gte("administered_at", startDate)
        .order("administered_at", { ascending: true }),
      supabase
        .from("session_ratings")
        .select("created_at, mood_before, mood_after, session_helpfulness, therapeutic_alliance")
        .eq("client_id", clientId)
        .gte("created_at", startDate)
        .order("created_at", { ascending: true })
    ]);

    if (assessmentsRes.data) setAssessments(assessmentsRes.data);
    if (ratingsRes.data) setSessionRatings(ratingsRes.data);
    setLoading(false);
  };

  const getAssessmentTypes = () => {
    const types = [...new Set(assessments.map(a => a.assessment_type))];
    return types;
  };

  const getChartData = () => {
    const filtered = selectedAssessment === "all" 
      ? assessments 
      : assessments.filter(a => a.assessment_type === selectedAssessment);

    return filtered.map(a => ({
      date: format(parseISO(a.administered_at), "MMM d"),
      score: a.total_score,
      type: a.assessment_type,
      severity: a.severity_level
    }));
  };

  const getMoodChartData = () => {
    return sessionRatings.map(r => ({
      date: format(parseISO(r.created_at), "MMM d"),
      preMood: r.mood_before || 0,
      postMood: r.mood_after || 0,
      helpfulness: r.session_helpfulness || 0,
      alliance: r.therapeutic_alliance || 0,
      moodImprovement: (r.mood_after || 0) - (r.mood_before || 0)
    }));
  };

  const calculateTrend = () => {
    if (assessments.length < 2) return { trend: "neutral", percentage: 0 };
    const recent = assessments.slice(-3);
    const older = assessments.slice(0, 3);
    const recentAvg = recent.reduce((acc, a) => acc + a.total_score, 0) / recent.length;
    const olderAvg = older.reduce((acc, a) => acc + a.total_score, 0) / older.length;
    const change = ((recentAvg - olderAvg) / olderAvg) * 100;
    
    // For most assessments, lower is better (PHQ-9, GAD-7)
    return {
      trend: change < -5 ? "improving" : change > 5 ? "worsening" : "stable",
      percentage: Math.abs(change).toFixed(1)
    };
  };

  const getAverageSessionImprovement = () => {
    if (sessionRatings.length === 0) return 0;
    const improvements = sessionRatings.map(r => (r.mood_after || 0) - (r.mood_before || 0));
    return (improvements.reduce((a, b) => a + b, 0) / improvements.length).toFixed(1);
  };

  const getAverageHelpfulness = () => {
    if (sessionRatings.length === 0) return 0;
    return (sessionRatings.reduce((acc, r) => acc + (r.session_helpfulness || 0), 0) / sessionRatings.length).toFixed(1);
  };

  const exportReport = () => {
    toast({
      title: "Report Generated",
      description: "PDF report has been generated and downloaded."
    });
  };

  const trend = calculateTrend();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Controls */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="3m">3 months</SelectItem>
              <SelectItem value="6m">6 months</SelectItem>
              <SelectItem value="12m">12 months</SelectItem>
            </SelectContent>
          </Select>
          <Select value={selectedAssessment} onValueChange={setSelectedAssessment}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="All assessments" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Assessments</SelectItem>
              {getAssessmentTypes().map(type => (
                <SelectItem key={type} value={type}>{type}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button onClick={exportReport} className="gap-2">
          <FileDown className="h-4 w-4" />
          Export PDF
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-card/50 border-border/30">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Overall Trend</p>
                <p className="text-2xl font-bold text-foreground capitalize">{trend.trend}</p>
                <p className="text-xs text-muted-foreground">{trend.percentage}% change</p>
              </div>
              {trend.trend === "improving" ? (
                <TrendingDown className="h-8 w-8 text-green-500" />
              ) : trend.trend === "worsening" ? (
                <TrendingUp className="h-8 w-8 text-destructive" />
              ) : (
                <Minus className="h-8 w-8 text-muted-foreground" />
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card/50 border-border/30">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Assessments</p>
                <p className="text-2xl font-bold text-foreground">{assessments.length}</p>
                <p className="text-xs text-muted-foreground">completed</p>
              </div>
              <Target className="h-8 w-8 text-primary opacity-80" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card/50 border-border/30">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Avg Session Impact</p>
                <p className="text-2xl font-bold text-foreground">+{getAverageSessionImprovement()}</p>
                <p className="text-xs text-muted-foreground">mood improvement</p>
              </div>
              <Activity className="h-8 w-8 text-accent opacity-80" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card/50 border-border/30">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Avg Helpfulness</p>
                <p className="text-2xl font-bold text-foreground">{getAverageHelpfulness()}/10</p>
                <p className="text-xs text-muted-foreground">client rating</p>
              </div>
              <Calendar className="h-8 w-8 text-secondary opacity-80" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Assessment Scores Chart */}
      {assessments.length > 0 && (
        <Card className="bg-card/50 border-border/30">
          <CardHeader>
            <CardTitle>Assessment Scores Over Time</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={getChartData()}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: "hsl(var(--card))", 
                      border: "1px solid hsl(var(--border))",
                      borderRadius: "8px"
                    }}
                    labelStyle={{ color: "hsl(var(--foreground))" }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="score" 
                    stroke="hsl(var(--primary))" 
                    strokeWidth={2}
                    dot={{ fill: "hsl(var(--primary))", strokeWidth: 2 }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Session Mood Improvement Chart */}
      {sessionRatings.length > 0 && (
        <Card className="bg-card/50 border-border/30">
          <CardHeader>
            <CardTitle>Session-by-Session Mood Changes</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={getMoodChartData()}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: "hsl(var(--card))", 
                      border: "1px solid hsl(var(--border))",
                      borderRadius: "8px"
                    }}
                  />
                  <Legend />
                  <Area 
                    type="monotone" 
                    dataKey="preMood" 
                    name="Pre-Session Mood"
                    stroke="hsl(var(--muted-foreground))" 
                    fill="hsl(var(--muted-foreground) / 0.2)" 
                  />
                  <Area 
                    type="monotone" 
                    dataKey="postMood" 
                    name="Post-Session Mood"
                    stroke="hsl(var(--primary))" 
                    fill="hsl(var(--primary) / 0.3)" 
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Therapeutic Alliance & Helpfulness */}
      {sessionRatings.length > 0 && (
        <Card className="bg-card/50 border-border/30">
          <CardHeader>
            <CardTitle>Therapeutic Alliance & Session Helpfulness</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={getMoodChartData()}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} domain={[0, 10]} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: "hsl(var(--card))", 
                      border: "1px solid hsl(var(--border))",
                      borderRadius: "8px"
                    }}
                  />
                  <Legend />
                  <Bar dataKey="helpfulness" name="Helpfulness" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="alliance" name="Therapeutic Alliance" fill="hsl(var(--accent))" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {assessments.length === 0 && sessionRatings.length === 0 && (
        <Card className="bg-card/50 border-border/30">
          <CardContent className="py-12 text-center">
            <Target className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-foreground mb-2">No Progress Data Yet</h3>
            <p className="text-muted-foreground">
              Administer assessments and collect session ratings to see visual progress reports.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
